import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Menu, X, LogIn, UserPlus, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { OwnerDashboardReminder } from "@/components/vg2/OwnerDashboardReminder";
import { useOwnerSignupPanel } from "@/components/vg2/OwnerSignupPanel";
import { ScrollToTopButton } from "@/components/vg2/ScrollToTopButton";

/**
 * Vg2Shell — corporate ValutaGenio identity (Phase 3B.1).
 * White canvas, anthracite text, red accent, official logo asset,
 * 720px content column, generous whitespace, no gradients, no cards.
 *
 * M8.3 · Vg2BackButton — universal safety-net back link on every VG2
 * step. Prefers `navigate(-1)` so form state and journey progress stay
 * intact; falls back to an explicit path when the history stack is
 * shallow (deep link / bookmark opening). Rendered above the content
 * column, low-emphasis, so it never competes with the primary CTA but
 * is always reachable from every error/loading/dead-end state.
 */
function _fallbackFor(pathname, params) {
  // Explicit fallback map keyed on the current VG2 route. Only invoked
  // when navigate(-1) is a no-op (history stack of length 1).
  const jid = params.journeyId;
  const cid = params.caseId;
  const aid = params.analysisId;
  if (/^\/vg2\/eva\//.test(pathname)) return `/vg2/agisci/${cid}`;
  if (/^\/vg2\/agisci\//.test(pathname)) return `/vg2/decidi/${cid}`;
  if (/^\/vg2\/decidi\//.test(pathname)) {
    // We don't have the journeyId inside DECIDI's params; the workspace
    // case view is the safest reachable-from-anywhere landing.
    return `/vg2/workspace/cases/${cid}`;
  }
  if (/^\/vg2\/valuta\/[^/]+\/risultato\//.test(pathname) && jid) {
    return `/vg2/capisci/${jid}/risultato`;
  }
  if (/^\/vg2\/valuta\/[^/]+\/transizione/.test(pathname) && jid) {
    return `/vg2/capisci/${jid}/risultato`;
  }
  if (/^\/vg2\/valuta\//.test(pathname) && jid) {
    return `/vg2/capisci/${jid}/risultato`;
  }
  if (/^\/vg2\/capisci\//.test(pathname)) return `/valuta`;
  if (pathname === "/vg2" || pathname === "/vg2/") return `/`;
  return `/`;
}

function Vg2BackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const onBack = React.useCallback(() => {
    const before = window.location.pathname + window.location.search;
    // Prefer real browser history so form state on the previous step
    // is preserved by React Router's default caching.
    try {
      navigate(-1);
    } catch { /* ignore */ }
    // If nothing changed after a tick, treat the stack as empty and
    // route to the explicit fallback for this step.
    window.setTimeout(() => {
      const after = window.location.pathname + window.location.search;
      if (after === before) {
        navigate(_fallbackFor(location.pathname, params));
      }
    }, 90);
  }, [navigate, location.pathname, params]);

  // Hide on the top-level entry surface — nowhere to go back to.
  if (location.pathname === "/vg2" || location.pathname === "/vg2/") return null;

  return (
    <button
      type="button"
      onClick={onBack}
      data-testid="vg2-back-button"
      className="inline-flex items-center gap-1.5 text-[13px] text-vg2-muted hover:text-vg2-red transition-colors -ml-1 px-1 py-1"
      aria-label="Torna indietro"
    >
      <span aria-hidden>←</span>
      <span>Torna indietro</span>
    </button>
  );
}

export function Vg2Shell({ children }) {
  const { user, logout } = useAuth();
  const { openPanel, Panel } = useOwnerSignupPanel();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const closeMenu = React.useCallback(() => setMenuOpen(false), []);

  return (
    <div className="min-h-screen bg-vg2-canvas text-vg2-ink font-sans antialiased">
      <header className="border-b border-vg2-line">
        <div className="mx-auto max-w-[720px] px-6 py-5 flex items-center justify-between">
          <Link to="/"
            data-testid="vg2-header-home"
            className="inline-flex items-center gap-2">
            <img
              src="/brand/logo.png"
              alt="ValutaGenio"
              className="h-7 md:h-8 w-auto select-none"
              draggable="false"
            />
          </Link>
          <div className="flex items-center gap-4">
            <span data-testid="vg2-header-privacy"
                  className="text-[11px] uppercase tracking-[0.14em] text-vg2-muted hidden sm:inline">
              Privato
            </span>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Apri il menu"
              aria-expanded={menuOpen}
              data-testid="vg2-hamburger-open"
              className="p-2 -mr-2 rounded hover:bg-vg2-subtle transition-colors"
            >
              <Menu size={22} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <HamburgerMenu
          user={user}
          onClose={closeMenu}
          onOpenSignup={() => { closeMenu(); openPanel(); }}
          onLogout={() => { logout(); closeMenu(); }}
        />
      )}

      <main className="mx-auto max-w-[720px] px-6">
        <div className="pt-4 pb-1 flex items-center justify-between gap-4 flex-wrap">
          <Vg2BackButton />
          <OwnerDashboardReminder />
        </div>
        {children}
      </main>
      <footer className="mx-auto max-w-[720px] px-6 py-10 mt-16 border-t border-vg2-line">
        <p className="text-xs text-vg2-muted">
          Le informazioni che inserisci restano private.
        </p>
      </footer>

      <Panel />
      <ScrollToTopButton />
    </div>
  );
}


function HamburgerMenu({ user, onClose, onOpenSignup, onLogout }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  const isOwner = user?.ruolo === "owner";

  return (
    <div className="fixed inset-0 z-[90]" role="dialog" aria-modal="true"
         data-testid="vg2-hamburger-menu">
      <button
        type="button"
        aria-label="Chiudi il menu"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        data-testid="vg2-hamburger-backdrop"
      />
      <aside
        className="
          absolute right-0 top-0 h-full w-full max-w-xs
          bg-white shadow-2xl flex flex-col
          animate-slide-in-right
        "
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-vg2-line">
          <span className="text-[11px] uppercase tracking-[0.22em] text-vg2-muted">
            Menu
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi il menu"
            className="p-1.5 rounded-full hover:bg-vg2-subtle text-neutral-500"
            data-testid="vg2-hamburger-close"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {isOwner ? (
            <>
              <MenuItem
                icon={<LayoutDashboard size={18} strokeWidth={1.6} />}
                label="La tua scrivania"
                to="/proprietari/dashboard"
                onClick={onClose}
                testid="vg2-hamburger-dashboard"
              />
              <MenuButton
                icon={<LogOut size={18} strokeWidth={1.6} />}
                label={`Esci (${user?.nome || "profilo"})`}
                onClick={onLogout}
                testid="vg2-hamburger-logout"
              />
            </>
          ) : (
            <>
              <MenuItem
                icon={<LogIn size={18} strokeWidth={1.6} />}
                label="Accedi"
                to="/proprietari/login"
                onClick={onClose}
                testid="vg2-hamburger-login"
              />
              <MenuButton
                icon={<UserPlus size={18} strokeWidth={1.6} />}
                label="Crea profilo gratuito"
                onClick={onOpenSignup}
                testid="vg2-hamburger-signup"
              />
            </>
          )}
        </nav>
        <div className="border-t border-vg2-line px-6 py-4">
          <p className="text-[11px] uppercase tracking-[0.14em] text-vg2-muted">
            ValutaGenio
          </p>
          <p className="text-xs text-vg2-muted mt-1">
            Le informazioni che inserisci restano private.
          </p>
        </div>
      </aside>
    </div>
  );
}


function MenuItem({ icon, label, to, onClick, testid }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testid}
      className="
        flex items-center gap-3 px-6 py-3
        text-vg2-ink hover:bg-vg2-subtle transition-colors
      "
    >
      <span className="text-vg2-muted">{icon}</span>
      <span className="text-sm">{label}</span>
    </Link>
  );
}


function MenuButton({ icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="
        w-full flex items-center gap-3 px-6 py-3 text-left
        text-vg2-ink hover:bg-vg2-subtle transition-colors
      "
    >
      <span className="text-vg2-muted">{icon}</span>
      <span className="text-sm">{label}</span>
    </button>
  );
}
